import React, { ReactNode } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Grid,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

interface SummaryCardProps {
  forecast?: {
    positivePct: number;
    negativePct: number;
    neutralPct: number;
    score: number;
    confidence: string;
    driversPositive: string[];
    driversNegative: string[];
    eventType: string;
    baseline: string;
    delta: string;
    recommendations?: string;
    replies1d?: number;
    replies7d?: number;
    likes7d?: number;
  };
  driversPositive?: string[];
  driversNegative?: string[];
  notableDeviations?: string[];
  imageDescription?: string;
  platform?: string;
  sender?: string;
  titleComponent?: ReactNode;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ 
  forecast, 
  driversPositive, 
  driversNegative, 
  notableDeviations, 
  imageDescription,
  platform, 
  sender, 
  titleComponent 
}) => {
  // Use drivers either from forecast or from direct props
  const positiveDrivers = driversPositive || forecast?.driversPositive || [];
  const negativeDrivers = driversNegative || forecast?.driversNegative || [];

  // Calculate engagement counts based on percentages
  const posCount = forecast?.replies7d ? Math.round(forecast.replies7d * forecast.positivePct / 100) : 0;
  const negCount = forecast?.replies7d ? Math.round(forecast.replies7d * forecast.negativePct / 100) : 0;
  const neuCount = forecast?.replies7d ? forecast.replies7d - posCount - negCount : 0;

  // Function to capitalize first letter of a string
  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <Card elevation={3}>
      <CardContent>
        {titleComponent || (
          <Typography variant="h5" gutterBottom>
            Performance Summary
          </Typography>
        )}
        
        {!forecast && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No forecast data available
          </Typography>
        )}
        
        {forecast && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Event Type
              </Typography>
              <Tooltip title="The category of content you're posting, which affects how audiences typically respond" arrow>
                <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                  <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              {capitalizeFirstLetter(forecast.eventType)}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Audience Baseline
              </Typography>
              <Tooltip title="How your typical audience usually responds to this type of content" arrow>
                <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                  <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              {forecast.baseline}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Analysis
              </Typography>
              <Tooltip title="How your specific content is expected to perform compared to the baseline" arrow>
                <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                  <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" paragraph sx={{ ml: 2 }}>
              {forecast.delta}
            </Typography>
            
            {/* Image Analysis Section */}
            {(imageDescription || (notableDeviations && notableDeviations.length > 0)) && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Media Description
                  </Typography>
                  <Tooltip title="Key elements detected in your media that may influence audience reactions" arrow>
                    <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                      <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                {/* Display the image description if available */}
                {imageDescription && (
                  <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                    {imageDescription}
                  </Typography>
                )}
                
                {/* Display notable deviations if available */}
                {notableDeviations && notableDeviations.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: imageDescription ? 2 : 0 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', ml: 1 }}>
                        Notable Deviations
                      </Typography>
                      <Tooltip title="Unusual elements in your media that may affect audience reactions" arrow>
                        <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                          <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box 
                      component="ul" 
                      sx={{ marginLeft: '16px' }}
                    >
                      {notableDeviations.map((item, index) => (
                        <li key={index}>
                          <Typography variant="body2">{item}</Typography>
                        </li>
                      ))}
                    </Box>
                  </>
                )}
              </>
            )}
            
            {/* Recommendations Section */}
            {forecast.recommendations && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Recommendations
                  </Typography>
                  <Tooltip title="Actionable suggestions to improve audience reception of your content" arrow>
                    <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                      <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                  {forecast.recommendations}
                </Typography>
              </>
            )}
            
            {/* Engagement Metrics Section - moved to the bottom */}
            {(forecast.replies1d !== undefined || forecast.replies7d !== undefined || forecast.likes7d !== undefined) && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Engagement Forecast
                  </Typography>
                  <Tooltip title="Engagement figures are forecasts based on historical averages. Actual results may vary with algorithm changes or ad spend." arrow>
                    <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                      <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={2} sx={{ ml: 0.5, mb: 2 }}>
                  {forecast.replies1d !== undefined && (
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.05)' }}>
                        <Typography variant="subtitle2">First 24h Replies</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{forecast.replies1d}</Typography>
                      </Paper>
                    </Grid>
                  )}
                  {forecast.replies7d !== undefined && (
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.05)' }}>
                        <Typography variant="subtitle2">7-Day Replies</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{forecast.replies7d}</Typography>
                      </Paper>
                    </Grid>
                  )}
                  {forecast.likes7d !== undefined && (
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.05)' }}>
                        <Typography variant="subtitle2">7-Day Likes</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{forecast.likes7d}</Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </Box>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(6, 214, 160, 0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#06D6A0', fontWeight: 'bold' }}>
                  Positive Drivers
                </Typography>
                <Tooltip title="Specific aspects of your content that are likely to generate positive reactions" arrow>
                  <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                    <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {positiveDrivers.length > 0 ? (
                <ul>
                  {positiveDrivers.map((driver, index) => (
                    <li key={index}>
                      <Typography variant="body2">{driver}</Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2">No positive drivers identified</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 107, 107, 0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#FF6B6B', fontWeight: 'bold' }}>
                  Negative Drivers
                </Typography>
                <Tooltip title="Specific aspects of your content that may trigger negative reactions" arrow>
                  <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                    <InfoIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {negativeDrivers.length > 0 ? (
                <ul>
                  {negativeDrivers.map((driver, index) => (
                    <li key={index}>
                      <Typography variant="body2">{driver}</Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2">No negative drivers identified</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SummaryCard; 